import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { billingAPI } from '../../lib/api';
import { FALLBACK_TIER_CATALOG } from '../../lib/tierCatalog';
import { getTierDisplayName, getTierExtraFeatures } from '../../lib/pricingDisplay';
import { useSubscription } from '../../context/SubscriptionContext';
import { useI18n } from '../../i18n';
import { toast } from 'sonner';

export default function UpgradePrompt() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { upgradePrompt, closeUpgrade } = useSubscription();
  const [tierCatalog, setTierCatalog] = useState([]);

  useEffect(() => {
    if (!upgradePrompt) return;
    billingAPI
      .getTiers()
      .then((res) => setTierCatalog(res.data.tiers || []))
      .catch(() => setTierCatalog(FALLBACK_TIER_CATALOG.tiers));
  }, [upgradePrompt]);

  if (!upgradePrompt) return null;

  const upgradeSlug = upgradePrompt.upgrade_to || 'starter';
  const upgradeTier = tierCatalog.find((t) => t.slug === upgradeSlug);
  const price = upgradeTier?.price_monthly_usd;
  const tierName = upgradeTier ? getTierDisplayName(t, upgradeTier) : upgradeSlug;
  const tierExtras = upgradeTier ? getTierExtraFeatures(t, upgradeTier) : [];

  const handleUpgradeNow = async () => {
    if (upgradeSlug === 'enterprise') {
      window.location.href = 'mailto:sales@domio.world?subject=Domio%20Enterprise';
      return;
    }
    try {
      const res = await billingAPI.createCheckout({ tier: upgradeSlug, billing_cycle: 'monthly' });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t('pricing.upgrade.checkoutFailed');
      toast.error(typeof msg === 'string' ? msg : t('pricing.upgrade.billingNotConfigured'));
      navigate('/app/pricing');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && closeUpgrade()}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" />
            {t('pricing.upgrade.title')}
          </DialogTitle>
          <DialogDescription>
            {upgradePrompt.detail || t('pricing.upgrade.defaultMessage')}
          </DialogDescription>
        </DialogHeader>
        {upgradeTier && (
          <div className="rounded-lg border border-domio-100 bg-domio-50/50 p-4">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-domio-600" />
              {tierName}
            </p>
            {price != null && (
              <p className="text-2xl font-bold text-domio-700 mt-1">
                ${price}
                <span className="text-sm font-normal text-slate-500">{t('pricing.upgrade.perMonth')}</span>
              </p>
            )}
            <ul className="mt-2 text-xs text-slate-600 space-y-1">
              {tierExtras.slice(0, 4).map((f) => (
                <li key={f}>• {f}</li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => { closeUpgrade(); navigate('/app/pricing'); }}>
            {t('pricing.upgrade.viewAllPlans')}
          </Button>
          <Button className="bg-domio-600 hover:bg-domio-700" onClick={handleUpgradeNow}>
            {t('pricing.upgrade.upgradeNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
